import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../AuthContext';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import "../../assets/CSS/Dashboard.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export default function ManagerRemediation() {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab] = useState('live'); // 'live' or 'escalations'

  // Filters State
  const [searchQuery, setSearchQuery] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (!user) {
      window.location.href = "/";
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/overview`);
      if (res.data.success) {
        setTasks(res.data.tasks);
        setTickets(res.data.escalated_tickets);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch manager data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTimeElapsed = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 60000); // mins
    if (diff < 60) return `${diff} mins ago`;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}h ${mins}m ago`;
  };

  // Derive unique workers for dropdown
  const uniqueWorkers = useMemo(() => {
    const workers = new Map();
    tasks.forEach(t => {
      if (t.worker_id && !workers.has(t.worker_id)) {
        workers.set(t.worker_id, `${t.First_Name} ${t.Last_Name}`.trim());
      }
    });
    return Array.from(workers.entries()).map(([id, name]) => ({ id, name }));
  }, [tasks]);

  // Apply Filters to Tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = 
        t.lot_id?.toLowerCase().includes(q) || 
        t.process_name?.toLowerCase().includes(q) ||
        t.First_Name?.toLowerCase().includes(q) ||
        t.worker_id?.toLowerCase().includes(q);

      let matchesWorker = true;
      if (workerFilter) {
        matchesWorker = t.worker_id === workerFilter;
      }
      return matchesSearch && matchesWorker;
    });
  }, [tasks, searchQuery, workerFilter]);

  return (
    <div className="d-flex dashboard">
      {/* ── SIDEBAR ── */}
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        {/* ── HEADER ── */}
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        {/* ── MAIN CONTENT AREA ── */}
        <main className="main-container p-0 pb-5" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>
          
          {/* PAGE BANNER */}
          <div style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            padding: "40px 48px", color: "#fff",
            position: "relative",
            overflow: "hidden"
          }}>
            <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, background: "linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(239,68,68,0.2) 100%)", borderRadius: "50%", filter: "blur(40px)" }}></div>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
              <div style={{ background: "rgba(239, 68, 68, 0.2)", padding: "10px", borderRadius: "12px", marginRight: "16px" }}>
                <i className="bi bi-shield-lock-fill text-danger" style={{ fontSize: "24px" }}></i> 
              </div>
              Manager Escalation Console
            </h1>
            <p style={{ margin: "12px 0 0 64px", opacity: 0.8, fontSize: 16, fontWeight: 500, maxWidth: "600px" }}>
              Monitor live worker activity across all departments and action SLA-breached permanent tickets immediately.
            </p>
          </div>

          <div className="container-fluid mt-4" style={{ maxWidth: "1400px", padding: "0 40px" }}>
            
            {/* TABS */}
            <ul className="nav nav-pills mb-4" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "10px" }}>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'live' ? 'active' : ''}`} 
                  onClick={() => setActiveTab('live')}
                  style={{ 
                    fontWeight: 700, 
                    borderRadius: "10px", 
                    padding: "10px 24px",
                    background: activeTab === 'live' ? "#0f172a" : "transparent",
                    color: activeTab === 'live' ? "#fff" : "#64748b"
                  }}
                >
                  <i className="bi bi-activity me-2"></i> Live Floor Status
                  <span className="badge ms-2" style={{ background: activeTab === 'live' ? "rgba(255,255,255,0.2)" : "#e2e8f0", color: activeTab === 'live' ? "#fff" : "#475569" }}>
                    {filteredTasks.length}
                  </span>
                </button>
              </li>
              <li className="nav-item ms-3">
                <button 
                  className={`nav-link ${activeTab === 'escalations' ? 'active' : ''}`} 
                  onClick={() => setActiveTab('escalations')}
                  style={{ 
                    fontWeight: 700, 
                    borderRadius: "10px", 
                    padding: "10px 24px",
                    background: activeTab === 'escalations' ? "#dc2626" : "transparent",
                    color: activeTab === 'escalations' ? "#fff" : "#64748b"
                  }}
                >
                  <i className="bi bi-exclamation-triangle-fill me-2"></i> Escalations
                  {tickets.length > 0 && (
                    <span className="badge ms-2 bg-white text-danger">
                      {tickets.length}
                    </span>
                  )}
                </button>
              </li>
            </ul>

            {loading ? (
              <div className="text-center" style={{ padding: "100px 0" }}>
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}></div>
                <h5 className="mt-4 text-secondary" style={{ fontWeight: 600 }}>Syncing floor data...</h5>
              </div>
            ) : (
              <div>
                {/* TAB 1: LIVE WORKER STATUS */}
                {activeTab === 'live' && (
                  <div>
                    {/* FILTERS SECTION */}
                    <div className="card border-0 mb-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)", background: "#fff" }}>
                      <div className="card-body p-4 d-flex gap-3 flex-wrap">
                        <div style={{ flex: "1 1 300px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Search Active Floor</label>
                          <div className="input-group">
                            <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                            <input 
                              type="text" 
                              className="form-control border-start-0 ps-0" 
                              placeholder="Lot ID, Process, Worker Name or ID..." 
                              value={searchQuery}
                              onChange={e => setSearchQuery(e.target.value)}
                              style={{ boxShadow: "none" }}
                            />
                          </div>
                        </div>

                        <div style={{ flex: "1 1 250px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Worker</label>
                          <select 
                            className="form-select" 
                            value={workerFilter} 
                            onChange={e => setWorkerFilter(e.target.value)}
                            style={{ boxShadow: "none" }}
                          >
                            <option value="">All Workers</option>
                            {uniqueWorkers.map(w => (
                              <option key={w.id} value={w.id}>{w.name} ({w.id})</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0" style={{ borderRadius: "20px", overflow: "hidden", background: "#fff", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)" }}>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table align-middle mb-0" style={{ fontSize: "15px" }}>
                            <thead style={{ background: "#1e293b" }}>
                              <tr>
                                <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Worker Info</th>
                                <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Lot / Process</th>
                                <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Target</th>
                                <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Live Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTasks.length === 0 ? (
                                <tr><td colSpan="4" className="text-center text-muted" style={{ padding: "60px 0" }}>
                                  <i className="bi bi-inbox text-slate-300" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                                  <div className="mt-3" style={{ fontWeight: 600 }}>No active workers match your filters</div>
                                </td></tr>
                              ) : filteredTasks.map(t => (
                                <tr key={t.task_id} style={{ transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = "#f8fafc"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                                  <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>{t.First_Name} {t.Last_Name}</div>
                                    <div style={{ color: "#64748b", fontWeight: 500, fontSize: "13px", marginTop: "4px" }}>ID: {t.worker_id}</div>
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>{t.lot_id}</div>
                                    <div className="badge bg-slate-100 text-slate-600 mt-2" style={{ background: "#e2e8f0", color: "#334155", fontWeight: 600 }}>{t.process_name}</div>
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0", fontWeight: 800, color: "#3b82f6" }}>
                                    {t.target_qty > 0 ? `${t.target_qty} pcs` : '-'}
                                  </td>
                                  <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                                    {t.status === 'IN_PROGRESS' && (
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                        <span style={{ display: "flex", alignItems: "center", background: "#ecfdf5", color: "#059669", padding: "6px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px" }}>
                                          <span style={{ width: "8px", height: "8px", background: "#10b981", borderRadius: "50%", marginRight: "8px", boxShadow: "0 0 8px #10b981" }}></span> Running
                                        </span>
                                        <div style={{ fontSize: "13px", color: "#64748b", marginTop: "6px", fontWeight: 600, marginLeft: "4px" }}>{getTimeElapsed(t.start_time)}</div>
                                      </div>
                                    )}
                                    {t.status === 'UNABLE_TO_START' && (
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                        <span style={{ display: "flex", alignItems: "center", background: "#fef2f2", color: "#dc2626", padding: "6px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px" }}>
                                          <i className="bi bi-x-circle-fill me-2"></i> Blocked
                                        </span>
                                        <div style={{ fontSize: "13px", color: "#dc2626", marginTop: "6px", fontWeight: 600, marginLeft: "4px", background: "#fee2e2", padding: "4px 8px", borderRadius: "6px" }}>{t.unable_reason}</div>
                                      </div>
                                    )}
                                    {t.status === 'DONE' && (
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                        <span style={{ display: "flex", alignItems: "center", background: "#f0f9ff", color: "#0284c7", padding: "6px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px" }}>
                                          <i className="bi bi-check-all me-2"></i> Completed
                                        </span>
                                        <div style={{ fontSize: "13px", color: "#64748b", marginTop: "6px", fontWeight: 600, marginLeft: "4px" }}>Task Finished</div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: ESCALATIONS */}
                {activeTab === 'escalations' && (
                  <div className="card border-0" style={{ borderRadius: "20px", background: "linear-gradient(145deg, #ffffff, #fef2f2)", boxShadow: "0 10px 25px -5px rgba(239, 68, 68, 0.1), 0 8px 10px -6px rgba(239, 68, 68, 0.05)" }}>
                    <div className="card-header bg-transparent border-0" style={{ padding: "30px 30px 20px" }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 style={{ fontWeight: 800, color: "#991b1b", margin: 0, fontSize: "20px" }}>
                            <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i> Action Required
                          </h5>
                          <p style={{ color: "#7f1d1d", margin: "6px 0 0", fontSize: "14px", fontWeight: 500, opacity: 0.8 }}>Tickets that breached the 12-hour SLA require remediation.</p>
                        </div>
                      </div>
                    </div>
                    <div className="card-body p-0 px-4 pb-4">
                      {tickets.length === 0 ? (
                        <div className="text-center" style={{ padding: "80px 0" }}>
                          <div style={{ width: "80px", height: "80px", background: "#dcfce7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", boxShadow: "0 10px 15px -3px rgba(34, 197, 94, 0.2)" }}>
                            <i className="bi bi-check2 text-success" style={{ fontSize: "40px", strokeWidth: "2" }}></i>
                          </div>
                          <h5 className="mt-4" style={{ color: "#166534", fontWeight: 800 }}>All Clear!</h5>
                          <p style={{ color: "#15803d", fontWeight: 500, fontSize: "15px", marginTop: "8px" }}>There are no active escalations right now.</p>
                        </div>
                      ) : (
                        <div className="row g-4">
                          {tickets.map(t => (
                            <div key={t.ticket_id} className="col-xl-4 col-lg-6 col-md-12">
                              <div className="card border-0 h-100" style={{ borderRadius: "16px", background: "#fff", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)", border: "1px solid #fca5a5" }}>
                                <div className="card-body p-4">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="badge" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", padding: "6px 12px", borderRadius: "8px", fontWeight: 800 }}>
                                      TICKET #{t.ticket_id}
                                    </span>
                                    <span style={{ fontSize: "12px", fontWeight: 800, color: "#ef4444", textTransform: "uppercase", letterSpacing: "1px" }}>{t.status}</span>
                                  </div>
                                  <h5 style={{ fontWeight: 800, color: "#0f172a", margin: "0 0 12px 0", fontSize: "18px" }}>{t.lot_id}</h5>
                                  
                                  <div style={{ background: "#f8fafc", padding: "12px 16px", borderRadius: "12px", marginBottom: "16px" }}>
                                    <div style={{ display: "flex", marginBottom: "8px" }}>
                                      <span style={{ width: "80px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Issue:</span>
                                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.issue_type}</span>
                                    </div>
                                    <div style={{ display: "flex" }}>
                                      <span style={{ width: "80px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Dept:</span>
                                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.department}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="d-flex gap-2 mt-4">
                                    <button className="btn w-100" style={{ background: "#0f172a", color: "#fff", fontWeight: 700, borderRadius: "10px", padding: "12px", transition: "all 0.2s" }} onMouseOver={e => { e.currentTarget.style.background = "#334155"; e.currentTarget.style.transform = "translateY(-2px)"; }} onMouseOut={e => { e.currentTarget.style.background = "#0f172a"; e.currentTarget.style.transform = "translateY(0)"; }}>
                                      REMEDIATE TASK
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
